import { AnimationFactory } from "../types";

export const createLightningDischargeAnimation: AnimationFactory = ({
                                                                        ctx,
                                                                        canvas,
                                                                        startX,
                                                                        startY,
                                                                        endX,
                                                                        endY,
                                                                        startTime,
                                                                        scale
                                                                    }) => {
    const duration = 600;
    const explosionDuration = 400;
    const particlesCount = 30;

    const SCALE = scale
        type Discharge = {
        angle: number;
        length: number;
        offset: number;
    };

    let discharges: Discharge[] = [];

    const createDischarges = () => {
        discharges = [];
        for (let i = 0; i < particlesCount; i++) {
            discharges.push({
                angle: Math.random() * Math.PI * 2,
                length: (15 + Math.random() * 25) * SCALE,
                offset: (5 + Math.random() * 15) * SCALE,
            });
        }
    };

    const drawLightning = (
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        segments = 12,
        amplitude = 12,
        forkChance = 0.15,
        forkLengthFactor = 0.4
    ) => {
        amplitude *= SCALE;

        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        const normalX = -dy / distance;
        const normalY = dx / distance;

        ctx.beginPath();
        ctx.moveTo(x1, y1);

        for (let i = 1; i < segments; i++) {
            const t = i / segments;
            const px = x1 + dx * t;
            const py = y1 + dy * t;

            const offset =
                (Math.random() - 0.5) *
                2 *
                amplitude *
                (1 - Math.abs(t - 0.5));
            const ox = px + normalX * offset;
            const oy = py + normalY * offset;

            ctx.lineTo(ox, oy);

            // невеличкі відгалуження (forks)
            if (Math.random() < forkChance && t > 0.2 && t < 0.9) {
                const forkLength = (distance / segments) * forkLengthFactor;
                const sign = Math.random() < 0.5 ? -1 : 1;

                const fx =
                    ox +
                    (normalX * sign *
                        forkLength *
                        (0.7 + Math.random() * 0.6));
                const fy =
                    oy +
                    (normalY * sign *
                        forkLength *
                        (0.7 + Math.random() * 0.6));

                ctx.moveTo(ox, oy);
                ctx.lineTo(fx, fy);
                ctx.moveTo(ox, oy);
            }
        }

        ctx.lineTo(x2, y2);
        ctx.stroke();
    };

    return (now: number) => {
        const elapsed = now - startTime;
        const flightProgress = Math.min(elapsed / duration, 1);

        // інтенсивність (мерехтіння)
        let intensity =
            0.9 +
            0.15 * Math.sin(elapsed * 0.02) +
            (Math.random() - 0.5) * 0.1;
        if (intensity < 0.7) intensity = 0.7;
        if (intensity > 1.2) intensity = 1.2;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (flightProgress < 1) {
            const x = startX + (endX - startX) * flightProgress;
            const y = startY + (endY - startY) * flightProgress;

            // свічення голови
            const glowRadius = 10 * SCALE;
            const glowGradient = ctx.createRadialGradient(
                x,
                y,
                0,
                x,
                y,
                glowRadius
            );

            glowGradient.addColorStop(0, "rgba(255,255,255,1)");
            glowGradient.addColorStop(
                0.5,
                "rgba(180,220,255," + 0.8 * intensity + ")"
            );
            glowGradient.addColorStop(1, "rgba(0,150,255,0)");

            ctx.fillStyle = glowGradient;
            ctx.beginPath();
            ctx.arc(x, y, glowRadius, 0, Math.PI * 2);
            ctx.fill();

            // основна блискавка
            ctx.lineWidth = 3 * SCALE * intensity;
            ctx.strokeStyle = `rgba(150, 220, 255, ${0.9 * intensity})`;
            drawLightning(startX, startY, x, y, 14, 14);

            // ореол
            ctx.lineWidth = 6 * SCALE * intensity;
            ctx.strokeStyle = `rgba(80, 160, 255, ${0.4 * intensity})`;
            drawLightning(startX, startY, x, y, 10, 18, 0.1, 0.25);

            return true;
        }

        // фаза розрядів
        const explosionElapsed = elapsed - duration;
        const explosionProgress = Math.min(
            explosionElapsed / explosionDuration,
            1
        );

        if (discharges.length === 0) {
            createDischarges();
        }

        const alphaBase = (1 - explosionProgress) * intensity;

        // центральне свічення
        const centerGlowRadius =
            20 * SCALE * (1 - explosionProgress * 0.6);
        const centerGradient = ctx.createRadialGradient(
            endX,
            endY,
            0,
            endX,
            endY,
            centerGlowRadius
        );
        centerGradient.addColorStop(
            0,
            `rgba(255,255,255,${alphaBase})`
        );
        centerGradient.addColorStop(
            0.4,
            `rgba(150,220,255,${alphaBase * 0.8})`
        );
        centerGradient.addColorStop(1, `rgba(0,130,255,0)`);

        ctx.fillStyle = centerGradient;
        ctx.beginPath();
        ctx.arc(endX, endY, centerGlowRadius, 0, Math.PI * 2);
        ctx.fill();

        // розряди навколо точки
        for (const d of discharges) {
            const jitter = (Math.random() - 0.5) * 4;
            const length = d.length * (1 - explosionProgress * 0.7);

            const startRadius = d.offset;
            const sx =
                endX +
                Math.cos(d.angle + jitter * 0.03) * startRadius;
            const sy =
                endY +
                Math.sin(d.angle + jitter * 0.03) * startRadius;

            const ex = sx + Math.cos(d.angle) * length;
            const ey = sy + Math.sin(d.angle) * length;

            ctx.lineWidth = 2 * SCALE;
            ctx.strokeStyle = `rgba(180, 230, 255, ${alphaBase})`;
            ctx.beginPath();
            ctx.moveTo(sx, sy);
            ctx.lineTo(ex, ey);
            ctx.stroke();

            ctx.lineWidth = 1 * SCALE;
            ctx.strokeStyle = `rgba(160, 140, 255, ${
                alphaBase * 0.7
            })`;
            ctx.beginPath();
            ctx.moveTo(sx, sy);
            ctx.lineTo(ex, ey);
            ctx.stroke();
        }

        // додаткові спаркли навколо центру
        const sparksCount = 10;
        for (let i = 0; i < sparksCount; i++) {
            const r =
                (5 + Math.random() * 30) * SCALE * (1 - explosionProgress);
            const ang = Math.random() * Math.PI * 2;

            const sx = endX + Math.cos(ang) * r;
            const sy = endY + Math.sin(ang) * r;

            const sparkAlpha =
                alphaBase * (0.4 + Math.random() * 0.6);

            ctx.fillStyle = `rgba(200, 240, 255, ${sparkAlpha})`;
            ctx.beginPath();
            ctx.arc(sx, sy, 1.5 * SCALE, 0, Math.PI * 2);
            ctx.fill();
        }

        return explosionProgress < 1;
    };
};